import React from 'react';
import { X, Calendar, Clock, BookOpen, Target, Award, DollarSign } from 'lucide-react';

const CourseDetailModal = ({ isOpen, onClose, course, onRegister }) => {
  if (!isOpen || !course) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4 animate-fadeIn">
      <div className="bg-blue-50 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-700 to-blue-600 p-6 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 bg-blue-800 hover:bg-blue-900 rounded-full p-2 transition"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="pr-12">
            <h2 className="text-2xl md:text-3xl font-display font-bold mb-2">{course.title}</h2>
            <p className="text-blue-200 text-lg font-medium">{course.module}</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Badges */}
          <div className="flex flex-wrap gap-2 mb-6">
            <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-semibold">
              📂 {course.track}
            </span>
            {course.prerequisite && (
              <span className="bg-blue-400 text-blue-900 px-4 py-2 rounded-full text-sm font-semibold">
                ⚠️ Advanced
              </span>
            )}
            {course.popular && (
              <span className="bg-gradient-to-r from-cream-400 to-cream-500 text-blue-900 px-4 py-2 rounded-full text-sm font-bold">
                ⭐ Paling Diminati
              </span>
            )}
          </div>

          {/* Info Grid */}
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div className="bg-white p-4 rounded-xl border-2 border-blue-200">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Calendar className="w-5 h-5 text-blue-700" />
                </div>
                <div>
                  <p className="text-sm text-blue-600 font-medium">Jadwal Kelas</p>
                  <p className="text-base font-semibold text-blue-900">{course.date}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border-2 border-blue-200">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Clock className="w-5 h-5 text-blue-700" />
                </div>
                <div>
                  <p className="text-sm text-blue-600 font-medium">Durasi</p>
                  <p className="text-base font-semibold text-blue-900">{course.duration}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border-2 border-blue-200">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <DollarSign className="w-5 h-5 text-blue-700" />
                </div>
                <div>
                  <p className="text-sm text-blue-600 font-medium">Investasi</p>
                  <p className="text-xl font-bold text-blue-900">{course.price}</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-4 rounded-xl border-2 border-blue-200">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <Award className="w-5 h-5 text-blue-700" />
                </div>
                <div>
                  <p className="text-sm text-blue-600 font-medium">Metode</p>
                  <p className="text-base font-semibold text-blue-900">{course.method}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Material */}
          <div className="bg-white p-5 rounded-xl border-2 border-blue-200 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="w-5 h-5 text-blue-700" />
              <h3 className="text-lg font-bold text-blue-900">Materi yang Dipelajari</h3>
            </div>
            <p className="text-blue-700 leading-relaxed text-base">{course.material}</p>
          </div>

          {/* Outcome */}
          <div className="bg-gradient-to-br from-blue-50 to-cream-100 p-5 rounded-xl border-2 border-blue-200 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-5 h-5 text-blue-700" />
              <h3 className="text-lg font-bold text-blue-900">Yang Akan Kamu Dapatkan</h3>
            </div>
            <p className="text-blue-700 leading-relaxed text-base">{course.outcome}</p>
          </div>

          {/* Prerequisite */}
          {course.prerequisite && (
            <div className="bg-blue-200 p-5 rounded-xl border-2 border-cream-400 mb-4">
              <h3 className="text-base font-bold text-blue-900 mb-2">⚠️ Prasyarat</h3>
              <p className="text-blue-800 text-base">{course.prerequisite}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-white border-t-2 border-blue-200 p-6">
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-between">
            <div className="text-center sm:text-left">
              <p className="text-sm text-blue-600">Investasi Anda</p>
              <p className="text-3xl font-bold text-blue-900">{course.price}</p>
            </div>
            <button
              onClick={() => {
                onRegister(course);
                onClose();
              }}
              className="w-full sm:w-auto bg-blue-700 text-white px-8 py-4 rounded-xl hover:bg-blue-800 transition-all shadow-lg hover:shadow-xl font-bold text-lg"
            >
              Daftar Kelas Ini
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetailModal;
